import React from 'react'
import { useDispatch } from 'react-redux'
import { ErrorMessage } from 'formik'
import styles from '../../UpdateSiteModal.module.css'
import {
  DISPLAY_MODE_STANDARD,
  DISPLAY_MODE_PRESENTATION,
  GATED_MODE_EMAIL_GATE,
  GATED_MODE_PASSWORD,
  GATED_MODE_STANDARD,
  PDF_FILE,
  ZIP_FILE
} from '../../constants'
import { hasFeature, isPhpFile } from 'utils/general'
import { setShowPaymentModal } from 'actions'
import { PLANS } from 'constants/plans'
import { MAX_DIRECT_UPLOAD_SIZE } from 'services/create-update-site/pro'
import { F_EMAIL_GATE, F_PWD_PROTECTION, F_FEEDBACK_MODE } from 'constants/plans/constants'
import { AccessOption, Toggle, AccessModeDropdown } from './AccessToggle'
import {
  UPGRADE_PRESENTATION_PAYLOAD,
  UPGRADE_CAPTURE_EMAILS_PAYLOAD,
  UPGRADE_PASSWORD_PROTECT_PAYLOAD,
  UPGRADE_FEEDBACK_PAYLOAD
} from 'actions'
import { showUpgradeCardModal } from 'actions'

// Password input container style
const passwordInputContainerStyle = {
  backgroundColor: '#f9f9f9',
  padding: '15px',
  marginTop: '10px',
  marginBottom: '15px',
  borderRadius: '8px'
}

const AccessSettings = ({ uploadValues, toggleValues, setFieldValue, name, productId, sectionIcon }) => {
  const dispatch = useDispatch()

  // Access mode options
  const accessModeOptions = [
    {
      value: GATED_MODE_STANDARD,
      label: 'Public (No restrictions)',
      description: 'Anyone with the link can view'
    },
    {
      value: GATED_MODE_PASSWORD,
      label: 'Password Protected',
      description: 'Make your link private by adding a layer of security'
    },
    {
      value: GATED_MODE_EMAIL_GATE,
      label: 'Capture Emails',
      description: 'Capture potential leads by requiring visitors to enter their email address'
    }
  ]

  // Check if user has access to the selected feature
  const checkAccessModeFeature = (value) => {
    if (value === GATED_MODE_PASSWORD && !hasFeature(productId, F_PWD_PROTECTION)) {
      onUpgrade(PLANS.PRO.id, UPGRADE_PASSWORD_PROTECT_PAYLOAD)
      return false
    }

    if (value === GATED_MODE_EMAIL_GATE && !hasFeature(productId, F_EMAIL_GATE)) {
      onUpgrade(PLANS.PRO_U.id, UPGRADE_CAPTURE_EMAILS_PAYLOAD)
      return false
    }

    return true
  }

  // Handle access mode change with atomic update
  const handleAccessModeChange = (newMode) => {
    const updates = {
      gatedMode: newMode,
      enableEmailGate: newMode === GATED_MODE_EMAIL_GATE
    }

    // Handle password-related fields based on mode
    if (newMode !== GATED_MODE_PASSWORD) {
      updates.password = ''
      updates.passwordProtected = false
    } else {
      updates.passwordProtected = true
    }

    // Single atomic update - all related fields update together
    setFieldValue(name, {
      ...toggleValues,
      ...updates
    })
  }

  // Only render if there's a file selected and it's not a PHP file
  if (!uploadValues.selectedFile || isPhpFile(uploadValues.selectedFile)) {
    return null
  }

  const onUpgrade = (planId, upgradePayload) => {
    dispatch(
      showUpgradeCardModal({
        ...upgradePayload,
        onClick: () => dispatch(setShowPaymentModal({ showPaymentModal: true, upgradePlanId: planId }))
      })
    )
  }

  return (
    <div id="access_settings" className={styles.section}>
      <span className={styles.sectionTitle}>
        {sectionIcon || <img src="/assets/icons/settings.svg" alt="settings" height="24" width="24" className="op-6" />}
        <h3 className="mb-0 font-18">Access Settings</h3>
      </span>

      {/* Access Mode Dropdown */}
      <div className={styles.accessOptionMainWrapper}>
        <div className={styles.accessOptionMain}>
          <div className={`${styles.optionIcon} ${styles.publicIcon}`}>
            <img src="/assets/icons/globe-pdf.svg" alt="access" height="24" width="24" className="op-6" />
          </div>
          <div className={styles.optionDetails}>
            <div className={styles.optionTitle}>Access Mode</div>
            <div className={styles.optionDescription}>Control how visitors can access your content</div>
          </div>
        </div>
        <div className={styles.optionToggle}>
          <AccessModeDropdown
            options={accessModeOptions}
            value={toggleValues.gatedMode}
            onChange={handleAccessModeChange}
            checkFeatureAccess={checkAccessModeFeature}
          />
        </div>
      </div>

      {/* Password Input - Only show when password protection is selected */}
      {toggleValues.gatedMode === GATED_MODE_PASSWORD && (
        <div style={passwordInputContainerStyle}>
          <div className={styles.formGroup}>
            <label htmlFor="passwordInput">Password</label>
            <input
              id="passwordInput"
              className={styles.formControl}
              placeholder="Enter password"
              value={toggleValues.password}
              onChange={(e) => setFieldValue(`${name}.password`, e.target.value)}
              aria-label="Site Password"
            />
            <ErrorMessage name={`${name}.password`} component="div" className="validation-error text-center" />
          </div>
        </div>
      )}

      {/* Only show Presentation Mode for PDFs with no additional files and below max size */}
      {uploadValues.fileTypes?.includes(PDF_FILE) &&
        (!uploadValues.addedFiles || uploadValues.addedFiles.length === 0) &&
        uploadValues.selectedFile?.size <= MAX_DIRECT_UPLOAD_SIZE && (
          <AccessOption
            iconClass="presentationIcon"
            iconSrc="/assets/icons/presentation-pdf.svg"
            title="Presentation Mode"
            description="Displays PDF as a slideshow"
            control={
              <div className={styles.optionToggle}>
                <Toggle
                  checked={toggleValues.displayMode === DISPLAY_MODE_PRESENTATION}
                  onChange={(e) => {
                    // Check if user has access to this feature
                    if (!productId) {
                      onUpgrade(PLANS.TINY.id, UPGRADE_PRESENTATION_PAYLOAD)
                      return
                    }

                    const newMode = e.target.checked ? DISPLAY_MODE_PRESENTATION : DISPLAY_MODE_STANDARD
                    setFieldValue(`${name}.displayMode`, newMode)
                  }}
                  id="presentationModeToggle"
                  ariaLabel="Toggle presentation mode"
                />
              </div>
            }
          />
        )}

      {/* Hide from Search Engines option */}
      <AccessOption
        iconClass="searchIcon"
        iconSrc="/assets/icons/eye-purple.svg"
        title="Hide from Search Engines"
        description="Prevent link from being indexed by Search Engines"
        control={
          <div className={styles.optionToggle}>
            <Toggle
              checked={toggleValues.disableIndexing}
              onChange={(e) => {
                // Check if user has access to this feature
                if (!productId) {
                  dispatch(setShowPaymentModal({ showPaymentModal: true, upgradePlanId: PLANS.TINY.id }))
                  return
                }

                setFieldValue(`${name}.disableIndexing`, e.target.checked)
              }}
              id="hideFromSearchToggle"
              ariaLabel="Toggle hide from search engines"
            />
          </div>
        }
      />

      {/* Feedback Mode option */}
      <AccessOption
        iconClass="feedbackIcon"
        iconSrc="/assets/icons/feedback-comment.svg"
        title="Enable Comments"
        description="Allow visitors to comment on your work"
        control={
          <div className={styles.optionToggle}>
            <Toggle
              checked={toggleValues.feedbackMode}
              onChange={(e) => {
                // Check if user has access to this feature
                if (!hasFeature(productId, F_FEEDBACK_MODE)) {
                  onUpgrade(PLANS.PRO_U.id, UPGRADE_FEEDBACK_PAYLOAD)
                  return
                }

                setFieldValue(`${name}.feedbackMode`, e.target.checked)
              }}
              id="feedbackModeToggle"
              ariaLabel="Toggle feedback mode"
            />
          </div>
        }
      />

      {/* Disable PDF Downloads - only for PDF files */}
      {uploadValues.fileTypes.includes(PDF_FILE) && (
        <AccessOption
          iconClass="downloadsIcon"
          iconSrc="/assets/icons/download-pdf.svg"
          title="Disable Downloads"
          description="Prevent visitors from downloading your file"
          control={
            <div className={styles.optionToggle}>
              <Toggle
                checked={toggleValues.disableExportPdf}
                onChange={(e) => {
                  // Check if user has access to this feature
                  if (!productId) {
                    dispatch(setShowPaymentModal({ showPaymentModal: true, upgradePlanId: PLANS.PRO.id }))
                    return
                  }

                  setFieldValue(`${name}.disableExportPdf`, e.target.checked)
                }}
                id="disableDownloadsToggle"
                ariaLabel="Toggle disable downloads"
              />
            </div>
          }
        />
      )}

      {/* Error Configuration - only for ZIP files */}
      {uploadValues.fileTypes.length === 1 && uploadValues.fileTypes.includes(ZIP_FILE) && (
        <AccessOption
          iconClass="errorIcon"
          iconSrc="/assets/icons/error-pdf.svg"
          title="Error Configuration"
          description="Add a path to a custom 404 error page when files not found"
          control={
            <div className={styles.optionToggle}>
              <Toggle
                checked={toggleValues.siteConfiguration}
                onChange={(e) => {
                  // Check if user has access to this feature
                  if (!productId) {
                    dispatch(setShowPaymentModal({ showPaymentModal: true, upgradePlanId: PLANS.PRO.id }))
                    return
                  }

                  const isChecked = e.target.checked
                  setFieldValue(`${name}.siteConfiguration`, isChecked)

                  // Set default error path when toggled on
                  if (isChecked) {
                    setFieldValue(`${name}.errorPath`, '/index.html')
                  } else {
                    setFieldValue(`${name}.errorPath`, '/')
                  }
                }}
                id="errorConfigToggle"
                ariaLabel="Toggle error configuration"
              />
            </div>
          }
        />
      )}

      {/* Error Path Input - only show when error configuration is enabled */}
      {uploadValues.fileTypes.length === 1 &&
        uploadValues.fileTypes?.includes(ZIP_FILE) &&
        toggleValues.siteConfiguration && (
          <div style={passwordInputContainerStyle}>
            <div className={styles.formGroup}>
              <label htmlFor="errorPathInput">Error Path</label>
              <input
                type="text"
                id="errorPathInput"
                className={styles.formControl}
                placeholder="/index.html"
                value={toggleValues.errorPath}
                onChange={(e) => setFieldValue(`${name}.errorPath`, e.target.value)}
                aria-label="Error Path"
              />
              <ErrorMessage name={`${name}.errorPath`} component="div" className="validation-error text-center" />
            </div>
          </div>
        )}
    </div>
  )
}

export default AccessSettings
